import styled, { keyframes } from 'styled-components'

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(6px); }
  to { opacity: 1; transform: translateY(0); }
`

const modalPop = keyframes`
  from { opacity: 0; transform: scale(0.97) translateY(8px); }
  to { opacity: 1; transform: scale(1) translateY(0); }
`

const pulse = keyframes`
  0% { box-shadow: 0 0 0 0 rgba(16, 185, 129, 0.45); }
  70% { box-shadow: 0 0 0 8px rgba(16, 185, 129, 0); }
  100% { box-shadow: 0 0 0 0 rgba(16, 185, 129, 0); }
`

export const HubContainer = styled.div`
  display: flex;
  width: 100%;
  min-height: 100dvh;
  background: ${(props) => props.theme.background || (props.theme.name === 'light' ? '#f1f5f9' : '#0b1120')};
  color: ${(props) => props.theme.textPrimary || (props.theme.name === 'light' ? '#0f172a' : '#f8fafc')};
  font-family: 'Montserrat', sans-serif;
  box-sizing: border-box;
`

export const HubLayout = styled.div`
  display: flex;
  width: 100%;
  min-height: 100dvh;
  align-items: stretch;

  @media (max-width: 960px) {
    flex-direction: column;
  }
`

export const HubSidebar = styled.aside`
  display: flex;
  flex-direction: column;
  width: 285px;
  height: 100dvh;
  max-height: 100dvh;
  position: sticky;
  top: 0;
  align-self: flex-start;
  flex-shrink: 0;
  background: ${(props) =>
    props.theme.cardBackground || (props.theme.name === 'light' ? '#ffffff' : '#111c35')};
  border-right: 1px solid
    ${(props) =>
      props.theme.cardBorder ||
      (props.theme.name === 'light' ? '#e2e8f0' : 'rgba(255, 255, 255, 0.08)')};
  padding: 20px 16px;
  box-sizing: border-box;
  z-index: 100;
  box-shadow: ${(props) =>
    props.theme.name === 'light'
      ? '2px 0 16px rgba(15, 23, 42, 0.04)'
      : '4px 0 24px rgba(0, 0, 0, 0.25)'};

  @media (max-width: 960px) {
    width: 100%;
    height: auto;
    max-height: none;
    position: relative;
    border-right: none;
    border-bottom: 1px solid
      ${(props) =>
        props.theme.cardBorder ||
        (props.theme.name === 'light' ? '#e2e8f0' : 'rgba(255, 255, 255, 0.08)')};
    padding: 16px;
  }
`

export const SidebarBackBtn = styled.button`
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  min-height: 42px;
  padding: 8px 12px;
  margin-bottom: 14px;
  border-radius: 10px;
  border: 1px solid
    ${(props) =>
      props.theme.name === 'light' ? 'rgba(37, 99, 235, 0.25)' : 'rgba(56, 189, 248, 0.28)'};
  background: ${(props) =>
    props.theme.name === 'light' ? 'rgba(37, 99, 235, 0.06)' : 'rgba(56, 189, 248, 0.1)'};
  color: ${(props) => (props.theme.name === 'light' ? '#1d4ed8' : '#38bdf8')};
  font-family: 'Montserrat', sans-serif;
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.18s ease;

  &:hover {
    background: ${(props) =>
      props.theme.name === 'light' ? 'rgba(37, 99, 235, 0.12)' : 'rgba(56, 189, 248, 0.18)'};
    transform: translateX(-2px);
  }
`

export const SidebarBrand = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding-bottom: 18px;
  margin-bottom: 16px;
  border-bottom: 1px solid
    ${(props) => (props.theme.name === 'light' ? '#e2e8f0' : 'rgba(255, 255, 255, 0.08)')};

  .brand-icon {
    width: 44px;
    height: 44px;
    border-radius: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: linear-gradient(135deg, rgba(16, 185, 129, 0.22), rgba(59, 130, 246, 0.18));
    border: 1px solid rgba(16, 185, 129, 0.4);
    color: ${(props) => (props.theme.name === 'light' ? '#059669' : '#34d399')};
    font-size: 20px;
    flex-shrink: 0;
  }

  .brand-text {
    display: flex;
    flex-direction: column;
    gap: 2px;

    strong {
      font-size: 14px;
      font-weight: 800;
      color: ${(props) => (props.theme.name === 'light' ? '#0f172a' : '#f8fafc')};
      letter-spacing: -0.2px;
    }

    span {
      font-size: 11px;
      font-weight: 600;
      color: ${(props) => (props.theme.name === 'light' ? '#64748b' : '#94a3b8')};
    }
  }
`

export const SidebarNavList = styled.nav`
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex: 1;

  @media (max-width: 960px) {
    flex-direction: row;
    overflow-x: auto;
    padding-bottom: 4px;
  }
`

export const SidebarNavItem = styled.button<{ $active?: boolean }>`
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  min-height: 50px;
  padding: 10px 12px;
  border-radius: 12px;
  border: 1px solid
    ${(props) =>
      props.$active
        ? props.theme.name === 'light'
          ? '#2563eb'
          : 'rgba(59, 130, 246, 0.55)'
        : 'transparent'};
  background: ${(props) =>
    props.$active
      ? props.theme.name === 'light'
        ? 'linear-gradient(135deg, rgba(37, 99, 235, 0.1), rgba(16, 185, 129, 0.08))'
        : 'linear-gradient(135deg, rgba(37, 99, 235, 0.24), rgba(16, 185, 129, 0.14))'
      : 'transparent'};
  color: ${(props) =>
    props.$active
      ? props.theme.name === 'light'
        ? '#1d4ed8'
        : '#f8fafc'
      : props.theme.name === 'light'
        ? '#475569'
        : '#94a3b8'};
  cursor: pointer;
  text-align: left;
  font-family: 'Montserrat', sans-serif;
  transition: all 0.18s ease;

  &:hover {
    background: ${(props) =>
      props.$active
        ? props.theme.name === 'light'
          ? 'rgba(37, 99, 235, 0.14)'
          : 'rgba(37, 99, 235, 0.28)'
        : props.theme.name === 'light'
          ? '#f8fafc'
          : 'rgba(255, 255, 255, 0.05)'};
    color: ${(props) => (props.theme.name === 'light' ? '#0f172a' : '#ffffff')};
  }

  .nav-icon {
    width: 36px;
    height: 36px;
    border-radius: 10px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 17px;
    flex-shrink: 0;
    background: ${(props) =>
      props.$active
        ? props.theme.name === 'light'
          ? '#2563eb'
          : 'rgba(59, 130, 246, 0.3)'
        : props.theme.name === 'light'
          ? '#f1f5f9'
          : 'rgba(255, 255, 255, 0.06)'};
    color: ${(props) =>
      props.$active
        ? '#ffffff'
        : props.theme.name === 'light'
          ? '#475569'
          : '#cbd5e1'};
  }

  .nav-text {
    display: flex;
    flex-direction: column;
    gap: 2px;
    overflow: hidden;

    .title {
      font-size: 13px;
      font-weight: 700;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    .desc {
      font-size: 11px;
      font-weight: 500;
      opacity: 0.8;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
  }

  @media (max-width: 960px) {
    width: auto;
    min-width: 195px;
    flex-shrink: 0;
  }
`

export const SidebarUserFooter = styled.div`
  position: relative;
  margin-top: auto;
  padding-top: 14px;
  border-top: 1px solid
    ${(props) => (props.theme.name === 'light' ? '#e2e8f0' : 'rgba(255, 255, 255, 0.08)')};
  display: flex;
  flex-direction: column;
  gap: 10px;
`

export const HubUserCard = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  border-radius: 12px;
  background: ${(props) =>
    props.theme.name === 'light' ? '#f8fafc' : 'rgba(0, 0, 0, 0.28)'};
  border: 1px solid
    ${(props) =>
      props.theme.name === 'light' ? '#e2e8f0' : 'rgba(255, 255, 255, 0.12)'};
  cursor: pointer;
  transition: all 0.2s ease;
  user-select: none;

  &:hover {
    background: ${(props) =>
      props.theme.name === 'light' ? '#eff6ff' : 'rgba(255, 255, 255, 0.08)'};
    border-color: rgba(166, 193, 11, 0.5);
  }

  .avatar {
    width: 38px;
    height: 38px;
    min-width: 38px;
    border-radius: 50%;
    background: #002b80;
    border: 2px solid #a6c10b;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #ffffff;
    font-size: 13px;
    font-weight: 700;
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.25);
  }

  .user-info {
    display: flex;
    flex-direction: column;
    overflow: hidden;
    flex: 1;

    .nickname {
      font-size: 13px;
      font-weight: 700;
      color: ${(props) => (props.theme.name === 'light' ? '#0f172a' : '#ffffff')};
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    .role {
      font-size: 10px;
      color: ${(props) => (props.theme.name === 'light' ? '#059669' : '#a6c10b')};
      font-weight: 700;
      text-transform: uppercase;
    }
  }

  .chevron-indicator {
    display: flex;
    align-items: center;
    justify-content: center;
    color: ${(props) => (props.theme.name === 'light' ? '#64748b' : '#94a3b8')};
    transition: transform 0.2s ease;
  }
`

export const HubUserDropdown = styled.div`
  position: absolute;
  bottom: calc(100% + 8px);
  left: 0;
  right: 0;
  width: 100%;
  background: ${(props) =>
    props.theme.name === 'light' ? '#ffffff' : '#001842'};
  border: 1px solid
    ${(props) =>
      props.theme.name === 'light' ? '#cbd5e1' : 'rgba(255, 255, 255, 0.16)'};
  border-radius: 12px;
  box-shadow: ${(props) =>
    props.theme.name === 'light'
      ? '0 12px 30px rgba(15, 23, 42, 0.18)'
      : '0 12px 30px rgba(0, 0, 0, 0.55)'};
  padding: 6px;
  display: flex;
  flex-direction: column;
  gap: 3px;
  z-index: 100000;
  backdrop-filter: blur(14px);
  animation: ${fadeIn} 0.18s ease-out;

  @media (max-width: 960px) {
    bottom: auto;
    top: calc(100% + 8px);
  }

  .dropdown-item {
    display: flex;
    align-items: center;
    gap: 10px;
    width: 100%;
    min-height: 40px;
    padding: 9px 12px;
    background: transparent;
    border: none;
    border-radius: 8px;
    cursor: pointer;
    font-family: 'Montserrat', sans-serif;
    font-size: 13px;
    font-weight: 600;
    color: ${(props) => (props.theme.name === 'light' ? '#1e293b' : '#f8fafc')};
    text-decoration: none;
    transition: all 0.18s ease;
    text-align: left;
    box-sizing: border-box;

    &:hover {
      background: ${(props) =>
        props.theme.name === 'light' ? '#f1f5f9' : 'rgba(255, 255, 255, 0.1)'};
      color: ${(props) => (props.theme.name === 'light' ? '#0f172a' : '#ffffff')};
    }

    &.logout-action {
      color: #ef4444;

      &:hover {
        background: rgba(239, 68, 68, 0.14);
        color: #dc2626;
      }
    }
  }

  .dropdown-divider {
    height: 1px;
    background: ${(props) =>
      props.theme.name === 'light' ? '#e2e8f0' : 'rgba(255, 255, 255, 0.1)'};
    margin: 3px 0;
  }
`

export const RoleBadge = styled.div<{ $role: string }>`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 7px 12px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  background: ${(props) => {
    if (props.$role === 'administrador') return 'rgba(245, 158, 11, 0.16)'
    if (props.$role === 'suporte') return 'rgba(59, 130, 246, 0.16)'
    return 'rgba(16, 185, 129, 0.16)'
  }};
  color: ${(props) => {
    if (props.$role === 'administrador') {
      return props.theme.name === 'light' ? '#b45309' : '#fbbf24'
    }
    if (props.$role === 'suporte') {
      return props.theme.name === 'light' ? '#1d4ed8' : '#60a5fa'
    }
    return props.theme.name === 'light' ? '#047857' : '#34d399'
  }};
  border: 1px solid
    ${(props) => {
      if (props.$role === 'administrador') return 'rgba(245, 158, 11, 0.4)'
      if (props.$role === 'suporte') return 'rgba(59, 130, 246, 0.4)'
      return 'rgba(16, 185, 129, 0.4)'
    }};
`

export const HubMainArea = styled.main`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 18px;
  padding: 24px 28px 48px;
  max-width: 1480px;
  width: 100%;
  box-sizing: border-box;
  overflow-x: hidden;
  animation: ${fadeIn} 0.22s ease-out;

  @media (max-width: 768px) {
    padding: 16px 12px 90px;
  }
`

export const DocNoticeBanner = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 14px 18px;
  border-radius: 14px;
  background: ${(props) =>
    props.theme.name === 'light'
      ? 'linear-gradient(135deg, #eff6ff 0%, #e0f2fe 100%)'
      : 'linear-gradient(135deg, rgba(37, 99, 235, 0.18) 0%, rgba(16, 185, 129, 0.14) 100%)'};
  border: 1px solid
    ${(props) => (props.theme.name === 'light' ? '#93c5fd' : 'rgba(59, 130, 246, 0.4)')};
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.06);

  .banner-left {
    display: flex;
    align-items: center;
    gap: 12px;

    .icon-box {
      width: 40px;
      height: 40px;
      border-radius: 10px;
      display: flex;
      align-items: center;
      justify-content: center;
      background: ${(props) =>
        props.theme.name === 'light' ? '#2563eb' : 'rgba(59, 130, 246, 0.25)'};
      color: #ffffff;
      font-size: 18px;
      flex-shrink: 0;
    }

    .text-box {
      display: flex;
      flex-direction: column;
      gap: 2px;

      strong {
        font-size: 13px;
        font-weight: 800;
        color: ${(props) => (props.theme.name === 'light' ? '#1e3a8a' : '#f8fafc')};
      }

      span {
        font-size: 12px;
        color: ${(props) => (props.theme.name === 'light' ? '#334155' : '#cbd5e1')};
        line-height: 1.4;
      }
    }
  }

  .banner-actions {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-shrink: 0;
  }

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;

    .banner-actions {
      justify-content: flex-end;
    }
  }
`

export const SectionHeaderBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
  padding: 18px 22px;
  border-radius: 16px;
  background: ${(props) =>
    props.theme.cardBackground || (props.theme.name === 'light' ? '#ffffff' : '#111c35')};
  border: 1px solid
    ${(props) =>
      props.theme.cardBorder ||
      (props.theme.name === 'light' ? '#e2e8f0' : 'rgba(255, 255, 255, 0.08)')};

  .title-block {
    display: flex;
    flex-direction: column;
    gap: 4px;

    h2 {
      margin: 0;
      font-size: 19px;
      font-weight: 800;
      display: flex;
      align-items: center;
      gap: 10px;
      color: ${(props) => (props.theme.name === 'light' ? '#0f172a' : '#f8fafc')};
    }

    p {
      margin: 0;
      font-size: 12px;
      color: ${(props) => (props.theme.name === 'light' ? '#475569' : '#94a3b8')};
      line-height: 1.45;
    }
  }

  .actions-block {
    display: flex;
    align-items: center;
    gap: 10px;
    flex-wrap: wrap;
  }
`

export const SurfaceCard = styled.div`
  background: ${(props) =>
    props.theme.cardBackground || (props.theme.name === 'light' ? '#ffffff' : '#111c35')};
  border: 1px solid
    ${(props) =>
      props.theme.cardBorder ||
      (props.theme.name === 'light' ? '#e2e8f0' : 'rgba(255, 255, 255, 0.08)')};
  border-radius: 16px;
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  box-shadow: ${(props) =>
    props.theme.name === 'light'
      ? '0 4px 18px rgba(15, 23, 42, 0.04)'
      : '0 8px 26px rgba(0, 0, 0, 0.22)'};
`

export const DataTableWrapper = styled.div`
  width: 100%;
  overflow-x: auto;
  border-radius: 12px;
  border: 1px solid
    ${(props) => (props.theme.name === 'light' ? '#e2e8f0' : 'rgba(255, 255, 255, 0.08)')};
`

export const StyledTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  min-width: 780px;
  font-size: 12px;

  thead {
    background: ${(props) =>
      props.theme.name === 'light' ? '#f8fafc' : 'rgba(15, 23, 42, 0.7)'};

    th {
      padding: 13px 14px;
      text-align: left;
      font-size: 11px;
      font-weight: 800;
      text-transform: uppercase;
      letter-spacing: 0.4px;
      color: ${(props) => (props.theme.name === 'light' ? '#475569' : '#94a3b8')};
      border-bottom: 1px solid
        ${(props) => (props.theme.name === 'light' ? '#e2e8f0' : 'rgba(255, 255, 255, 0.08)')};
      white-space: nowrap;
    }
  }

  tbody {
    tr {
      border-bottom: 1px solid
        ${(props) => (props.theme.name === 'light' ? '#f1f5f9' : 'rgba(255, 255, 255, 0.06)')};
      transition: background 0.15s ease;

      &:hover {
        background: ${(props) =>
          props.theme.name === 'light' ? '#f8fafc' : 'rgba(255, 255, 255, 0.025)'};
      }

      &:last-child {
        border-bottom: none;
      }
    }

    td {
      padding: 13px 14px;
      vertical-align: middle;
      color: ${(props) => (props.theme.name === 'light' ? '#1e293b' : '#e2e8f0')};
    }
  }
`

export const MethodBadge = styled.span<{ $method: string }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 3px 8px;
  border-radius: 6px;
  font-size: 10px;
  font-weight: 800;
  font-family: monospace;
  letter-spacing: 0.4px;
  background: ${(props) => {
    const m = props.$method.toUpperCase()
    if (m === 'GET') return 'rgba(16, 185, 129, 0.16)'
    if (m === 'POST') return 'rgba(59, 130, 246, 0.16)'
    if (m === 'PUT') return 'rgba(245, 158, 11, 0.18)'
    if (m === 'DELETE' || m === 'DEL') return 'rgba(239, 68, 68, 0.18)'
    return 'rgba(168, 85, 247, 0.18)'
  }};
  color: ${(props) => {
    const m = props.$method.toUpperCase()
    if (m === 'GET') return props.theme.name === 'light' ? '#047857' : '#34d399'
    if (m === 'POST') return props.theme.name === 'light' ? '#1d4ed8' : '#60a5fa'
    if (m === 'PUT') return props.theme.name === 'light' ? '#b45309' : '#fbbf24'
    if (m === 'DELETE' || m === 'DEL') return props.theme.name === 'light' ? '#b91c1c' : '#f87171'
    return props.theme.name === 'light' ? '#6d28d9' : '#c084fc'
  }};
  border: 1px solid
    ${(props) => {
      const m = props.$method.toUpperCase()
      if (m === 'GET') return 'rgba(16, 185, 129, 0.35)'
      if (m === 'POST') return 'rgba(59, 130, 246, 0.35)'
      if (m === 'PUT') return 'rgba(245, 158, 11, 0.35)'
      if (m === 'DELETE' || m === 'DEL') return 'rgba(239, 68, 68, 0.35)'
      return 'rgba(168, 85, 247, 0.35)'
    }};
`

export const UsageProgressWrapper = styled.div<{ $pct: number }>`
  display: flex;
  flex-direction: column;
  gap: 5px;
  min-width: 135px;

  .usage-label {
    display: flex;
    justify-content: space-between;
    font-size: 11px;
    font-weight: 700;
    font-variant-numeric: tabular-nums;
    color: ${(props) =>
      props.$pct >= 100
        ? '#ef4444'
        : props.$pct >= 80
          ? '#f59e0b'
          : props.theme.name === 'light'
            ? '#0f172a'
            : '#f8fafc'};
  }

  .bar-bg {
    width: 100%;
    height: 7px;
    border-radius: 999px;
    background: ${(props) =>
      props.theme.name === 'light' ? '#e2e8f0' : 'rgba(255, 255, 255, 0.1)'};
    overflow: hidden;

    .bar-fill {
      height: 100%;
      width: ${(props) => Math.min(100, Math.max(0, props.$pct))}%;
      border-radius: 999px;
      background: ${(props) =>
        props.$pct >= 100
          ? '#ef4444'
          : props.$pct >= 80
            ? '#f59e0b'
            : 'linear-gradient(90deg, #10b981, #3b82f6)'};
      transition: width 0.25s ease;
    }
  }
`

export const CipherBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 9px;
  border-radius: 7px;
  font-size: 11px;
  font-family: monospace;
  font-weight: 700;
  background: ${(props) =>
    props.theme.name === 'light' ? '#f0fdf4' : 'rgba(16, 185, 129, 0.12)'};
  color: ${(props) => (props.theme.name === 'light' ? '#047857' : '#34d399')};
  border: 1px solid rgba(16, 185, 129, 0.35);
`

export const StatusTag = styled.span<{ $active: boolean; $warn?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 700;
  background: ${(props) =>
    props.$warn
      ? 'rgba(239, 68, 68, 0.14)'
      : props.$active
        ? 'rgba(16, 185, 129, 0.15)'
        : 'rgba(148, 163, 184, 0.15)'};
  color: ${(props) =>
    props.$warn
      ? props.theme.name === 'light'
        ? '#b91c1c'
        : '#f87171'
      : props.$active
        ? props.theme.name === 'light'
          ? '#047857'
          : '#34d399'
        : props.theme.name === 'light'
          ? '#475569'
          : '#94a3b8'};
`

export const ActionBtn = styled.button<{
  $variant?: 'primary' | 'secondary' | 'danger' | 'success'
  $small?: boolean
}>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
  min-height: ${(props) => (props.$small ? '36px' : '44px')};
  padding: ${(props) => (props.$small ? '6px 12px' : '10px 16px')};
  border-radius: 10px;
  font-size: ${(props) => (props.$small ? '11px' : '12px')};
  font-weight: 700;
  font-family: 'Montserrat', sans-serif;
  cursor: pointer;
  transition: all 0.18s ease;
  border: 1px solid transparent;
  white-space: nowrap;

  ${(props) => {
    const v = props.$variant || 'primary'
    if (v === 'primary') {
      return `
        background: linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%);
        color: #ffffff;
        box-shadow: 0 4px 12px rgba(37, 99, 235, 0.25);
        &:hover:not(:disabled) {
          transform: translateY(-1px);
          box-shadow: 0 6px 16px rgba(37, 99, 235, 0.35);
        }
      `
    }
    if (v === 'success') {
      return `
        background: linear-gradient(135deg, #10b981 0%, #059669 100%);
        color: #ffffff;
        box-shadow: 0 4px 12px rgba(16, 185, 129, 0.25);
        &:hover:not(:disabled) {
          transform: translateY(-1px);
        }
      `
    }
    if (v === 'danger') {
      return `
        background: rgba(239, 68, 68, 0.12);
        color: ${props.theme.name === 'light' ? '#dc2626' : '#f87171'};
        border-color: rgba(239, 68, 68, 0.3);
        &:hover:not(:disabled) {
          background: rgba(239, 68, 68, 0.2);
        }
      `
    }
    return `
      background: ${props.theme.name === 'light' ? '#f1f5f9' : 'rgba(255, 255, 255, 0.06)'};
      color: ${props.theme.name === 'light' ? '#1e293b' : '#e2e8f0'};
      border-color: ${props.theme.name === 'light' ? '#cbd5e1' : 'rgba(255, 255, 255, 0.14)'};
      &:hover:not(:disabled) {
        background: ${props.theme.name === 'light' ? '#e2e8f0' : 'rgba(255, 255, 255, 0.12)'};
      }
    `
  }}

  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }
`

/* ============================================================================
 * MODAIS E SUB-MODAIS ("MODAL DO MODAL")
 * ============================================================================ */
export const ModalOverlay = styled.div<{ $zIndex?: number }>`
  position: fixed;
  inset: 0;
  z-index: ${(props) => (props.$zIndex ? props.$zIndex + 100000 : 100100)};
  background: rgba(2, 6, 23, 0.72);
  backdrop-filter: blur(5px);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  box-sizing: border-box;

  @media (max-width: 768px) {
    padding: 0;
    align-items: flex-end;
  }
`

export const ModalCard = styled.div<{ $maxWidth?: string }>`
  width: 100%;
  max-width: ${(props) => props.$maxWidth || '680px'};
  max-height: 90dvh;
  background: ${(props) =>
    props.theme.cardBackground || (props.theme.name === 'light' ? '#ffffff' : '#0f172a')};
  color: ${(props) => (props.theme.name === 'light' ? '#0f172a' : '#f8fafc')};
  border: 1px solid
    ${(props) => (props.theme.name === 'light' ? '#cbd5e1' : 'rgba(255, 255, 255, 0.14)')};
  border-radius: 18px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  box-shadow: 0 24px 60px rgba(0, 0, 0, 0.45);
  animation: ${modalPop} 0.2s ease-out;

  @media (max-width: 768px) {
    max-width: 100%;
    max-height: 94dvh;
    border-radius: 20px 20px 0 0;
  }
`

export const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 18px 22px;
  border-bottom: 1px solid
    ${(props) => (props.theme.name === 'light' ? '#e2e8f0' : 'rgba(255, 255, 255, 0.08)')};
  background: ${(props) =>
    props.theme.name === 'light' ? '#f8fafc' : 'rgba(15, 23, 42, 0.6)'};

  .modal-title {
    display: flex;
    flex-direction: column;
    gap: 3px;

    h3 {
      margin: 0;
      font-size: 16px;
      font-weight: 800;
      display: flex;
      align-items: center;
      gap: 9px;
      color: ${(props) => (props.theme.name === 'light' ? '#0f172a' : '#f8fafc')};
    }

    span {
      font-size: 12px;
      color: ${(props) => (props.theme.name === 'light' ? '#64748b' : '#94a3b8')};
    }
  }

  .close-btn {
    width: 38px;
    height: 38px;
    border-radius: 10px;
    border: 1px solid
      ${(props) => (props.theme.name === 'light' ? '#cbd5e1' : 'rgba(255, 255, 255, 0.12)')};
    background: transparent;
    color: ${(props) => (props.theme.name === 'light' ? '#475569' : '#cbd5e1')};
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    font-size: 18px;

    &:hover {
      background: rgba(239, 68, 68, 0.12);
      color: #ef4444;
      border-color: rgba(239, 68, 68, 0.3);
    }
  }
`

export const ModalBody = styled.div`
  padding: 20px 22px;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 16px;
  flex: 1;
`

export const ModalFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 10px;
  padding: 15px 22px;
  border-top: 1px solid
    ${(props) => (props.theme.name === 'light' ? '#e2e8f0' : 'rgba(255, 255, 255, 0.08)')};
  background: ${(props) =>
    props.theme.name === 'light' ? '#f8fafc' : 'rgba(15, 23, 42, 0.6)'};
  flex-wrap: wrap;
`

export const SubModalTriggerCard = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 14px;
  padding: 14px 16px;
  border-radius: 12px;
  background: ${(props) =>
    props.theme.name === 'light' ? '#f8fafc' : 'rgba(15, 23, 42, 0.55)'};
  border: 1px solid
    ${(props) => (props.theme.name === 'light' ? '#cbd5e1' : 'rgba(59, 130, 246, 0.3)')};

  .info {
    display: flex;
    flex-direction: column;
    gap: 4px;

    strong {
      font-size: 13px;
      font-weight: 800;
      color: ${(props) => (props.theme.name === 'light' ? '#0f172a' : '#f8fafc')};
    }

    span {
      font-size: 12px;
      color: ${(props) => (props.theme.name === 'light' ? '#475569' : '#94a3b8')};
    }
  }

  @media (max-width: 640px) {
    flex-direction: column;
    align-items: stretch;
  }
`

export const MethodTabsBar = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  padding-bottom: 8px;
  border-bottom: 1px solid
    ${(props) => (props.theme.name === 'light' ? '#e2e8f0' : 'rgba(255, 255, 255, 0.08)')};
`

export const MethodTabBtn = styled.button<{ $active: boolean; $method?: string }>`
  display: inline-flex;
  align-items: center;
  gap: 7px;
  min-height: 40px;
  padding: 8px 14px;
  border-radius: 10px;
  font-size: 12px;
  font-weight: 800;
  font-family: 'Montserrat', sans-serif;
  cursor: pointer;
  transition: all 0.16s ease;
  border: 1px solid
    ${(props) =>
      props.$active
        ? '#2563eb'
        : props.theme.name === 'light'
          ? '#cbd5e1'
          : 'rgba(255, 255, 255, 0.12)'};
  background: ${(props) =>
    props.$active
      ? '#2563eb'
      : props.theme.name === 'light'
        ? '#ffffff'
        : 'rgba(255, 255, 255, 0.04)'};
  color: ${(props) =>
    props.$active ? '#ffffff' : props.theme.name === 'light' ? '#334155' : '#cbd5e1'};

  .count-pill {
    padding: 2px 7px;
    border-radius: 999px;
    font-size: 10px;
    background: ${(props) =>
      props.$active
        ? 'rgba(255, 255, 255, 0.22)'
        : props.theme.name === 'light'
          ? '#f1f5f9'
          : 'rgba(255, 255, 255, 0.08)'};
  }
`

export const FormRow = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 14px;

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`

export const FieldGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;

  label {
    font-size: 12px;
    font-weight: 700;
    color: ${(props) => (props.theme.name === 'light' ? '#334155' : '#cbd5e1')};
  }

  small {
    font-size: 11px;
    color: ${(props) => (props.theme.name === 'light' ? '#64748b' : '#94a3b8')};
  }
`

export const StyledInput = styled.input`
  width: 100%;
  min-height: 44px;
  padding: 10px 14px;
  border-radius: 10px;
  border: 1px solid
    ${(props) => (props.theme.name === 'light' ? '#cbd5e1' : 'rgba(255, 255, 255, 0.14)')};
  background: ${(props) =>
    props.theme.name === 'light' ? '#f8fafc' : 'rgba(15, 23, 42, 0.65)'};
  color: ${(props) => (props.theme.name === 'light' ? '#0f172a' : '#f8fafc')};
  font-size: 13px;
  font-family: 'Montserrat', sans-serif;
  box-sizing: border-box;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;

  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.18);
  }
`

export const StyledSelect = styled.select`
  width: 100%;
  min-height: 44px;
  padding: 10px 14px;
  border-radius: 10px;
  border: 1px solid
    ${(props) => (props.theme.name === 'light' ? '#cbd5e1' : 'rgba(255, 255, 255, 0.14)')};
  background: ${(props) => (props.theme.name === 'light' ? '#f8fafc' : '#0f172a')};
  color: ${(props) => (props.theme.name === 'light' ? '#0f172a' : '#f8fafc')};
  font-size: 13px;
  font-family: 'Montserrat', sans-serif;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: #3b82f6;
  }

  option {
    background: ${(props) => (props.theme.name === 'light' ? '#ffffff' : '#0f172a')};
    color: ${(props) => (props.theme.name === 'light' ? '#0f172a' : '#f8fafc')};
  }
`

export const RouteSelectorBox = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-height: 360px;
  overflow-y: auto;
  padding: 10px;
  border-radius: 12px;
  border: 1px solid
    ${(props) => (props.theme.name === 'light' ? '#cbd5e1' : 'rgba(255, 255, 255, 0.12)')};
  background: ${(props) =>
    props.theme.name === 'light' ? '#f8fafc' : 'rgba(15, 23, 42, 0.5)'};
`

export const RouteItemLabel = styled.label<{ $checked: boolean }>`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 10px 12px;
  border-radius: 10px;
  cursor: pointer;
  background: ${(props) =>
    props.$checked
      ? props.theme.name === 'light'
        ? '#eff6ff'
        : 'rgba(37, 99, 235, 0.18)'
      : props.theme.name === 'light'
        ? '#ffffff'
        : 'rgba(255, 255, 255, 0.03)'};
  border: 1px solid
    ${(props) =>
      props.$checked
        ? '#3b82f6'
        : props.theme.name === 'light'
          ? '#e2e8f0'
          : 'rgba(255, 255, 255, 0.07)'};
  transition: all 0.15s ease;

  input {
    margin-top: 3px;
    width: 18px;
    height: 18px;
    accent-color: #2563eb;
    flex-shrink: 0;
  }

  .route-info {
    display: flex;
    flex-direction: column;
    gap: 3px;
    flex: 1;

    .top-line {
      display: flex;
      align-items: center;
      gap: 8px;
      flex-wrap: wrap;

      .route-path {
        font-family: monospace;
        font-size: 12px;
        font-weight: 700;
        color: ${(props) => (props.theme.name === 'light' ? '#0f172a' : '#f8fafc')};
      }
    }

    .route-desc {
      font-size: 11px;
      color: ${(props) => (props.theme.name === 'light' ? '#475569' : '#94a3b8')};
    }
  }
`

export const SecretKeyCreatedBox = styled.div`
  padding: 16px 18px;
  border-radius: 14px;
  background: ${(props) =>
    props.theme.name === 'light' ? '#ecfdf5' : 'rgba(16, 185, 129, 0.14)'};
  border: 1px solid rgba(16, 185, 129, 0.45);
  display: flex;
  flex-direction: column;
  gap: 10px;

  strong {
    font-size: 13px;
    color: ${(props) => (props.theme.name === 'light' ? '#065f46' : '#34d399')};
  }

  .key-display {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
    padding: 10px 12px;
    border-radius: 8px;
    background: ${(props) => (props.theme.name === 'light' ? '#ffffff' : '#0f172a')};
    border: 1px solid rgba(16, 185, 129, 0.35);
    font-family: monospace;
    font-size: 13px;
    font-weight: 700;
    word-break: break-all;
    color: ${(props) => (props.theme.name === 'light' ? '#0f172a' : '#f8fafc')};
  }
`

export const GisaChatCard = styled(SurfaceCard)`
  min-height: 500px;
  max-height: 680px;
  display: flex;
  flex-direction: column;
  padding: 0;
  overflow: hidden;

  .chat-topbar {
    padding: 16px 20px;
    border-bottom: 1px solid
      ${(props) => (props.theme.name === 'light' ? '#e2e8f0' : 'rgba(255, 255, 255, 0.08)')};
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    background: ${(props) =>
      props.theme.name === 'light'
        ? 'linear-gradient(135deg, #f8fafc 0%, #eff6ff 100%)'
        : 'linear-gradient(135deg, rgba(30, 41, 59, 0.9) 0%, rgba(15, 23, 42, 0.95) 100%)'};

    .gisa-brand {
      display: flex;
      align-items: center;
      gap: 12px;

      .avatar-glow {
        width: 42px;
        height: 42px;
        border-radius: 12px;
        background: linear-gradient(135deg, #8b5cf6 0%, #2563eb 100%);
        color: #ffffff;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 20px;
        font-weight: 800;
      }

      .info {
        display: flex;
        flex-direction: column;
        gap: 2px;

        h4 {
          margin: 0;
          font-size: 15px;
          font-weight: 800;
          color: ${(props) => (props.theme.name === 'light' ? '#0f172a' : '#f8fafc')};
        }

        span {
          font-size: 11px;
          color: ${(props) => (props.theme.name === 'light' ? '#475569' : '#94a3b8')};
        }
      }
    }
  }

  .chat-messages {
    flex: 1;
    overflow-y: auto;
    padding: 18px 20px;
    display: flex;
    flex-direction: column;
    gap: 14px;
  }

  .quick-prompts {
    display: flex;
    gap: 8px;
    padding: 0 20px 12px;
    overflow-x: auto;

    button {
      flex-shrink: 0;
      padding: 7px 12px;
      border-radius: 999px;
      font-size: 11px;
      font-weight: 700;
      font-family: 'Montserrat', sans-serif;
      cursor: pointer;
      border: 1px solid
        ${(props) => (props.theme.name === 'light' ? '#cbd5e1' : 'rgba(59, 130, 246, 0.35)')};
      background: ${(props) =>
        props.theme.name === 'light' ? '#f8fafc' : 'rgba(37, 99, 235, 0.12)'};
      color: ${(props) => (props.theme.name === 'light' ? '#1e40af' : '#93c5fd')};

      &:hover {
        background: ${(props) =>
          props.theme.name === 'light' ? '#eff6ff' : 'rgba(37, 99, 235, 0.24)'};
      }
    }
  }

  .chat-input-bar {
    padding: 14px 20px;
    border-top: 1px solid
      ${(props) => (props.theme.name === 'light' ? '#e2e8f0' : 'rgba(255, 255, 255, 0.08)')};
    display: flex;
    gap: 10px;
    background: ${(props) =>
      props.theme.name === 'light' ? '#f8fafc' : 'rgba(15, 23, 42, 0.6)'};
  }
`

export const ChatBubble = styled.div<{ $isUser: boolean }>`
  align-self: ${(props) => (props.$isUser ? 'flex-end' : 'flex-start')};
  max-width: 85%;
  padding: 12px 16px;
  border-radius: ${(props) =>
    props.$isUser ? '16px 16px 4px 16px' : '16px 16px 16px 4px'};
  background: ${(props) =>
    props.$isUser
      ? 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)'
      : props.theme.name === 'light'
        ? '#f1f5f9'
        : 'rgba(30, 41, 59, 0.95)'};
  color: ${(props) =>
    props.$isUser ? '#ffffff' : props.theme.name === 'light' ? '#0f172a' : '#f1f5f9'};
  border: 1px solid
    ${(props) =>
      props.$isUser
        ? 'transparent'
        : props.theme.name === 'light'
          ? '#cbd5e1'
          : 'rgba(255, 255, 255, 0.08)'};
  font-size: 13px;
  line-height: 1.55;
  white-space: pre-wrap;
`

export const KpiMiniGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 12px;

  @media (max-width: 900px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .kpi-box {
    padding: 12px 14px;
    border-radius: 12px;
    background: ${(props) =>
      props.theme.name === 'light' ? '#f8fafc' : 'rgba(15, 23, 42, 0.55)'};
    border: 1px solid
      ${(props) => (props.theme.name === 'light' ? '#e2e8f0' : 'rgba(255, 255, 255, 0.07)')};
    display: flex;
    flex-direction: column;
    gap: 3px;

    span {
      font-size: 11px;
      color: ${(props) => (props.theme.name === 'light' ? '#64748b' : '#94a3b8')};
      font-weight: 600;
    }

    strong {
      font-size: 15px;
      font-weight: 800;
      font-variant-numeric: tabular-nums;
      color: ${(props) => (props.theme.name === 'light' ? '#0f172a' : '#f8fafc')};
    }
  }
`

export const CcChipList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;

  .cc-chip {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 5px 10px;
    border-radius: 999px;
    font-size: 12px;
    font-weight: 600;
    background: ${(props) =>
      props.theme.name === 'light' ? '#eff6ff' : 'rgba(37, 99, 235, 0.18)'};
    color: ${(props) => (props.theme.name === 'light' ? '#1d4ed8' : '#93c5fd')};
    border: 1px solid rgba(59, 130, 246, 0.35);

    button {
      background: transparent;
      border: none;
      color: inherit;
      cursor: pointer;
      display: flex;
      align-items: center;
      padding: 0;
    }
  }
`

export const DocSectionBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;

  h4 {
    margin: 0;
    font-size: 15px;
    font-weight: 800;
    color: ${(props) => (props.theme.name === 'light' ? '#0f172a' : '#f8fafc')};
    display: flex;
    align-items: center;
    gap: 8px;
  }

  p,
  li {
    font-size: 13px;
    line-height: 1.6;
    color: ${(props) => (props.theme.name === 'light' ? '#334155' : '#cbd5e1')};
  }

  pre {
    margin: 0;
    padding: 14px;
    border-radius: 10px;
    background: ${(props) => (props.theme.name === 'light' ? '#0f172a' : '#020617')};
    color: #e2e8f0;
    font-family: monospace;
    font-size: 12px;
    overflow-x: auto;
    border: 1px solid rgba(255, 255, 255, 0.1);
  }
`

export const DocsSubTabsBar = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  padding: 8px;
  border-radius: 14px;
  background: ${(props) =>
    props.theme.name === 'light' ? '#ffffff' : 'rgba(17, 28, 53, 0.85)'};
  border: 1px solid
    ${(props) =>
      props.theme.name === 'light' ? '#e2e8f0' : 'rgba(255, 255, 255, 0.08)'};
  box-shadow: ${(props) =>
    props.theme.name === 'light'
      ? '0 4px 14px rgba(15, 23, 42, 0.04)'
      : '0 6px 20px rgba(0, 0, 0, 0.22)'};
`

export const DocsSubTabBtn = styled.button<{ $active?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  min-height: 42px;
  padding: 8px 16px;
  border-radius: 10px;
  border: 1px solid
    ${(props) =>
      props.$active
        ? props.theme.name === 'light'
          ? '#2563eb'
          : 'rgba(59, 130, 246, 0.6)'
        : 'transparent'};
  background: ${(props) =>
    props.$active
      ? props.theme.name === 'light'
        ? 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)'
        : 'linear-gradient(135deg, rgba(37, 99, 235, 0.35) 0%, rgba(16, 185, 129, 0.22) 100%)'
      : 'transparent'};
  color: ${(props) =>
    props.$active
      ? '#ffffff'
      : props.theme.name === 'light'
        ? '#475569'
        : '#94a3b8'};
  font-family: 'Montserrat', sans-serif;
  font-size: 12.5px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.18s ease;

  &:hover {
    background: ${(props) =>
      props.$active
        ? props.theme.name === 'light'
          ? '#1d4ed8'
          : 'rgba(37, 99, 235, 0.42)'
        : props.theme.name === 'light'
          ? '#f1f5f9'
          : 'rgba(255, 255, 255, 0.06)'};
    color: ${(props) =>
      props.$active
        ? '#ffffff'
        : props.theme.name === 'light'
          ? '#0f172a'
          : '#f8fafc'};
  }

  .tab-badge {
    padding: 2px 7px;
    border-radius: 999px;
    font-size: 10.5px;
    font-weight: 800;
    background: ${(props) =>
      props.$active
        ? 'rgba(255, 255, 255, 0.22)'
        : props.theme.name === 'light'
          ? '#e2e8f0'
          : 'rgba(255, 255, 255, 0.1)'};
    color: ${(props) =>
      props.$active
        ? '#ffffff'
        : props.theme.name === 'light'
          ? '#334155'
          : '#cbd5e1'};
  }
`

export const DocsCardsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 18px;

  @media (max-width: 960px) {
    grid-template-columns: 1fr;
  }
`

export const PulseIndicator = styled.span`
  width: 9px;
  height: 9px;
  border-radius: 50%;
  background: #10b981;
  display: inline-block;
  animation: ${pulse} 2s infinite;
`
